import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { PropertyCard } from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fetchProperties, fetchCities } from "@/lib/api";
import { PROPERTY } from "@/lib/testIds";

export default function ListingPage({ listingType, title, subtitle, heroImg }) {
  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["properties", listingType],
    queryFn: () => fetchProperties({ listing_type: listingType, limit: 100 }),
  });
  const { data: cities = [] } = useQuery({
    queryKey: ["cities"],
    queryFn: fetchCities,
  });

  const [query, setQuery] = useState("");
  const [city, setCity] = useState("all");
  const [nsfas, setNsfas] = useState(false);

  const filtered = useMemo(() => {
    return properties.filter((p) => {
      if (city !== "all" && p.city !== city) return false;
      if (nsfas && !p.nsfas_accredited) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !p.title.toLowerCase().includes(q) &&
          !p.city.toLowerCase().includes(q) &&
          !p.address.toLowerCase().includes(q) &&
          !(p.nearby_institution || "").toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [properties, city, nsfas, query]);

  const clearFilters = () => { setQuery(""); setCity("all"); setNsfas(false); };
  const hasFilters = query || city !== "all" || nsfas;

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-primary/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-primary-foreground w-full">
          <p className="overline text-secondary">{listingType === "leasing" ? "Student Accommodation" : "Investment Opportunities"}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif max-w-3xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-primary-foreground/85">{subtitle}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border bg-background sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search title, city or institution…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 rounded-sm h-11"
              data-testid="listing-search"
            />
          </div>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="w-[180px] rounded-sm h-11" data-testid={PROPERTY.filterCity}>
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm px-3 py-2 border border-input rounded-sm cursor-pointer h-11" data-testid={PROPERTY.filterNsfas}>
            <Checkbox checked={nsfas} onCheckedChange={(v) => setNsfas(!!v)} />
            NSFAS Accredited
          </label>
          {hasFilters && (
            <Button variant="ghost" onClick={clearFilters} data-testid={PROPERTY.clearFilters}>
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
          <div className="ml-auto text-xs text-muted-foreground">
            {isLoading ? "Loading…" : `${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {filtered.length === 0 && !isLoading ? (
          <div className="border border-border p-16 text-center">
            <p className="font-serif text-2xl">{properties.length === 0 ? "No properties listed yet." : "No properties match your filters."}</p>
            <p className="mt-2 text-muted-foreground">{properties.length === 0 ? "Check back soon — we're updating our portfolio." : "Try clearing filters or contact us for tailored options."}</p>
            {properties.length === 0 ? (
              <Button asChild className="mt-6 rounded-sm bg-primary text-primary-foreground" data-testid="empty-state-contact">
                <a href="/contact">Get in touch</a>
              </Button>
            ) : (
              <Button onClick={clearFilters} className="mt-6 rounded-sm">Clear filters</Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <PropertyCard key={p.id} property={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
